import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { registrationAPI } from '../services/api';
import adminService from '../services/adminService';
import { toast } from 'react-toastify';

const AdminPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication status
    if (!adminService.isLoggedIn()) {
      toast.error('Admin authentication required');
      navigate('/admin');
      return;
    }
    
    fetchRegistrations();
  }, [navigate]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const response = await registrationAPI.getAllRegistrations();
      setRegistrations(response.data);
      setTotalCount(response.total);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminService.logout();
    toast.info('Admin logged out successfully');
    navigate('/admin');
  };

  const exportToCSV = () => {
    if (registrations.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = [
      'ID', 'Name', 'Age', 'DOB', 'Sex', 'Designation', 'College', 
      'Email', 'Phone', 'Address', 'Subject', 'Research Area', 
      'Participant Type', 'Supervisor Recommendation', 'Fee', 'Payment Status', 
      'Session ID', 'Transaction ID', 'Payment Method', 'Payment Amount', 'Payment Currency',
      'Payment Date', 'Gateway Response', 'Registered At'
    ];

    const csvContent = [
      headers.join(','),
      ...registrations.map(reg => [
        reg.id,
        `"${reg.name}"`,
        reg.age || '',
        reg.dob,
        reg.sex,
        `"${reg.designation}"`,
        `"${reg.college}"`,
        reg.email,
        reg.phone,
        `"${reg.address?.replace(/"/g, '""') || ''}"`,
        `"${reg.subject}"`,
        `"${reg.research_area}"`,
        reg.participant_type,
        reg.supervisor_recommendation ? 'Yes' : 'No',
        reg.fee,
        reg.payment_status || 'Not Paid',
        reg.payment_session_id || 'N/A',
        reg.payment_transaction_id || 'N/A',
        reg.payment_method || 'N/A',
        reg.payment_amount || 'N/A',
        reg.payment_currency || 'INR',
        reg.payment_date ? new Date(reg.payment_date).toLocaleString() : 'N/A',
        `"${typeof reg.payment_gateway_response === 'string' ? reg.payment_gateway_response.replace(/"/g, '""') : 'N/A'}"`,
        new Date(reg.registered_at).toLocaleString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `NSDSST_2025_Registrations_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Data exported successfully!');
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading registrations...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row>
        <Col>
          <Card className="shadow">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <h3 className="mb-0">NSDSST 2025 - Registrations</h3>
                <small>Admin Dashboard</small>
              </div>
              <div className="d-flex align-items-center">
                <Badge bg="light" text="dark" className="fs-6 me-3">
                  Total: {totalCount}
                </Badge>
                <Button 
                  variant="outline-light" 
                  size="sm" 
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </Card.Header>
            
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Alert variant="info" className="mb-0 flex-grow-1 me-3">
                  <strong>{totalCount}</strong> participants registered for NSDSST 2025
                </Alert>
                <Button 
                  variant="success" 
                  onClick={exportToCSV}
                  disabled={registrations.length === 0}
                >
                  Export CSV
                </Button>
              </div>

              {registrations.length === 0 ? (
                <Alert variant="warning" className="text-center">
                  No registrations found.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-dark">
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        {/* <th>Phone</th> */}
                        <th>Designation</th>
                        <th>College</th>
                        <th>Type</th>
                        <th>Fee</th>
                        <th>Payment Status</th>
                        {/* <th>Session ID</th> */}
                        <th>Transaction ID</th>
                        <th>Payment Method</th>
                        {/* <th>Amount</th>
                        <th>Currency</th> */}
                        {/* <th>Payment Date</th> */}
                        {/* <th>Gateway Response</th> */}
                        <th>Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((registration) => (
                        <tr key={registration.id}>
                          <td>{registration.id}</td>
                          <td>
                            <strong>{registration.name}</strong>
                            <br />
                            <small className="text-muted">
                              {registration.sex}, Age: {registration.age || 'N/A'}
                            </small>
                          </td>
                          <td>{registration.email}
                            <br />
                            <small className="text-muted">
                              {registration.phone}
                            </small>
                          </td>
                          <td>{registration.designation}</td>
                          <td>
                            <small>{registration.college}</small>
                          </td>
                          <td>
                            <Badge 
                              bg={registration.participant_type === 'Student' ? 'primary' : 'success'}
                            >
                              {registration.participant_type}
                            </Badge>
                          </td>
                          <td>
                            <strong>₹{registration.fee}</strong>
                          </td>
                          <td>
                            <Badge 
                              bg={registration.payment_status === 'completed' ? 'success' : 
                                 registration.payment_status === 'Failed' ? 'danger' : 
                                 registration.payment_status === 'Pending' ? 'warning' : 'secondary'}
                            >
                              {registration.payment_status || 'Not Paid'}
                            </Badge>
                          </td>
                          {/* <td>
                            <small className="text-monospace">
                              {registration.payment_session_id || 'N/A'}
                            </small>
                          </td> */}
                          <td>
                            <small className="text-monospace">
                              {registration.payment_transaction_id || 'N/A'}
                            </small>
                          </td>
                          <td>
                            <small>
                              {registration.payment_method || 'N/A'}
                            </small>
                          </td>
                          {/* <td>
                            <small>
                              {registration.payment_amount ? `₹${registration.payment_amount}` : 'N/A'}
                            </small>
                          </td>
                          <td>
                            <small>
                              {registration.payment_currency || 'INR'}
                            </small>
                          </td> */}
                          {/* <td>
                            <small>
                              {registration.payment_date ? new Date(registration.payment_date).toLocaleDateString() : 'N/A'}
                            </small>
                          </td> */}
                          {/* <td>
                            <small className="text-truncate d-inline-block" style={{maxWidth: '150px'}} title={registration.payment_gateway_response || 'N/A'}>
                              {registration.payment_gateway_response || 'N/A'}
                            </small>
                          </td> */}
                          <td>
                            <small>
                              {new Date(registration.registered_at).toLocaleDateString()}
                              <br />
                              {new Date(registration.registered_at).toLocaleTimeString()}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminPage;
